import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/helpers/utils';
import { ArrowRight } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { asyncTrackingEvent, asyncTrackingEventProps } from '../../../app/utils/tracking';
import LoadMore from '../LoadMore';
import { SectionContainer } from './SectionContainer';
import { SectionParagraph, SectionStrapline, SectionTitle } from './SectionUtilities';

type Props = {
  bkgColor?: string;
  className?: string;
  paddingClassName?: string;
  spacingClassName?: string;
  indent?: boolean;
  rounded?: boolean;
  shadow?: boolean;
  title?: any;
  titleLarge?: boolean;
  strapline?: any;
  paragraph?: any;
  content?: any;
  button?: any;
  reverse?: boolean;
  reverseMobile?: boolean;
  multipleImages?: any;
  disableLoadMore?: boolean;
  tracking?: any;
  children?: any;
};

const SectionOneCol = ({
  bkgColor,
  className,
  paddingClassName,
  spacingClassName,
  indent = true,
  rounded = false,
  shadow = false,
  title,
  titleLarge = false,
  strapline,
  paragraph,
  content,
  button,
  reverse,
  reverseMobile,
  multipleImages,
  disableLoadMore,
  tracking,
  children
}: Props) => {
  const logosToShowInitial: number = 15;
  const logosToShowAll: number = 99;
  let logosToShow: any = [];

  const [itemsToShow, setItemsToShow] = useState([]);
  const [next, setNext] = useState(logosToShowInitial);

  const loopWithSlice = (start: any, end: any) => {
    const slicedItems = multipleImages
      ? (() => {
          return multipleImages?.slice(start, end);
        })()
      : [];
    logosToShow = [...itemsToShow, ...slicedItems];
    setItemsToShow(logosToShow);
  };

  useEffect(() => {
    loopWithSlice(0, logosToShowInitial);
  }, []);

  const showMore = (action: number) => {
    const event: asyncTrackingEventProps = {
      track: 'logo_splash_view_all_logos_click',
      trackAbacus: 'logo_splash_view_all_logos_click',
      trackText: 'View all',
      trackId: tracking.id,
      trackUrl: tracking.path
    };
    asyncTrackingEvent(event);
    loopWithSlice(next, next + action);
    setNext(next + action);
  };
  const logosTotal: number = Number(multipleImages?.length);

  const gridCols = (count: number) => {
    if (count > 0) {
      const mobileCols = count <= 2 ? 'grid-cols-' + count : count >= 3 ? 'grid-cols-2 sm:grid-cols-3' : '';
      const desktopCols = count <= 5 ? 'md:grid-cols-' + count : 'md:grid-cols-5';
      return mobileCols + ' ' + desktopCols;
    } else return false;
  };

  bkgColor === 'bg-transparent' && !paddingClassName && (paddingClassName = 'py-10 md:py-12');
  !paddingClassName && (paddingClassName = 'py-12 md:py-16');
  return (
    <SectionContainer
      width="full"
      bkgColor={bkgColor}
      disableIndent={!indent}
      containerClass={cn(rounded ? 'rounded-2xl' : '', shadow ? 'shadow-xl' : '')}
    >
      <SectionContainer
        containerClass={`${paddingClassName} ${className ? className : ''}`}
        disableSuppression={true}
        disableIndent={true}
      >
        {(title || strapline || paragraph) && (
          <div className={cn('flex flex-col items-center text-center space-y-2 mb-5 md:mb-8')}>
            {strapline && <SectionStrapline>{strapline}</SectionStrapline>}
            {title && (
              <SectionContainer width="md" disabledIndent={true} disableSuppression={true}>
                <SectionTitle large={titleLarge}>{title}</SectionTitle>
              </SectionContainer>
            )}
            {paragraph && <SectionParagraph>{paragraph}</SectionParagraph>}
          </div>
        )}
        {(content || children) && (
          <div
            className={`flex ${reverseMobile ? `flex-col-reverse` : 'flex-col'} ${
              reverse ? `md:flex-row-reverse` : ``
            } ${spacingClassName ? spacingClassName : ''}`}
          >
            {content || children}
          </div>
        )}
        {button && <div className="flex justify-center pt-10">{button}</div>}
        {itemsToShow && multipleImages && (
          <div className="section-container-lg suppress-section-container-padding text-center mb-5 md:mb-8">
            <div
              className={`grid gap-6 justify-center mx-auto max-w-[16rem] sm:max-w-[25rem] md:max-w-none ${gridCols(
                itemsToShow.length
              )}`}
            >
              <LoadMore images={itemsToShow} columns="1" />
            </div>
          </div>
        )}
        {!disableLoadMore && itemsToShow && multipleImages && (
          <div className="flex justify-center mt-7">
            {itemsToShow.length < logosTotal ? (
              <Button
                appearance={`primary`}
                variant={'outline'}
                onClick={() => showMore(logosToShowAll)}
                data-cy={`button:cta-section-one-col`}
              >
                <ArrowRight className="w-4" />
                <span>View all</span>
              </Button>
            ) : null}
          </div>
        )}
      </SectionContainer>
    </SectionContainer>
  );
};
export default SectionOneCol;
